import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CheckCircle2, FileCheck2, FileText, Globe2, Trash2, Upload } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { useTenant } from "@/components/AppShell";
import { Button, Card, Input } from "@/components/ui";
import { api, ApiError, canManage, type ExportDocumentSettings } from "@/lib/api";
import { fmtDateTime, fmtNumber } from "@/lib/format";
import { t } from "@/lib/i18n";

const errMsg = (e: unknown) => (e instanceof ApiError ? (e.body as { message?: string })?.message : undefined);
const toBase64 = (f: File) =>
  new Promise<string>((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(String(r.result).split(",")[1] ?? "");
    r.onerror = () => rej(r.error);
    r.readAsDataURL(f);
  });

/**
 * Ausfuhrbegleitdokument im Auftrag: hochladen (PDF), ansehen, entfernen, freigeben. Erscheint, sobald der Auftrag
 * deswegen wartet oder schon ein Dokument hat.
 */
export function OrderExportDocumentSection({ orderId, waiting }: { orderId: string; waiting: boolean }) {
  const { tenantId } = useTenant();
  const qc = useQueryClient();
  const fileRef = useRef<HTMLInputElement>(null);
  const q = useQuery({ queryKey: ["export-doc", tenantId, orderId], queryFn: () => api.orders.exportDocument(tenantId, orderId) });
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ["export-doc", tenantId, orderId] });
    setTimeout(() => void qc.invalidateQueries({ queryKey: ["order", tenantId, orderId] }), 1500);
  };
  const upload = useMutation({
    mutationFn: async (f: File) => api.orders.uploadExportDocument(tenantId, orderId, { filename: f.name, data: await toBase64(f) }),
    onSuccess: () => { toast.success(t("Dokument hochgeladen"), { description: t("Bitte prüfen und freigeben.") }); refresh(); },
    onError: (e) => toast.error(t("Nicht hochgeladen"), { description: errMsg(e) }),
  });
  const remove = useMutation({ mutationFn: (docId: string) => api.orders.deleteExportDocument(tenantId, orderId, docId), onSuccess: refresh, onError: (e) => toast.error(t("Nicht entfernt"), { description: errMsg(e) }) });
  const approve = useMutation({
    mutationFn: () => api.orders.approveExportDocument(tenantId, orderId),
    onSuccess: () => { toast.success(t("Freigegeben"), { description: t("Der Auftrag wird neu geprüft und geht danach ans Lager.") }); refresh(); },
    onError: (e) => toast.error(t("Nicht freigegeben"), { description: errMsg(e) }),
  });
  const d = q.data;
  if (!d || (!waiting && !d.documents.length && !d.approvedAt)) return null;

  return (
    <section className="rounded-xl border border-border p-4">
      <p className="font-semibold text-[13.5px] flex items-center gap-2"><Globe2 className="size-4 text-fg-muted" /> {t("Ausfuhrbegleitdokument")}</p>
      <p className="mt-1 text-[12.5px] text-fg-muted">{t("Ausfuhr in ein Drittland über der eingestellten Wert- oder Gewichtsgrenze (Standard 1.000 € / 1.000 kg): Das Dokument geht nach der Freigabe als Anhang mit dem Auftrag ans Lager.")}</p>
      {d.documents.length > 0 && (
        <ul className="mt-3 space-y-1.5">
          {d.documents.map((doc) => (
            <li key={doc.id} className="flex items-center gap-2 text-[13px]">
              <FileText className="size-4 text-fg-subtle shrink-0" />
              <a href={api.orders.exportDocumentUrl(tenantId, orderId, doc.id)} target="_blank" rel="noreferrer" className="truncate hover:underline">{doc.filename}</a>
              <span className="text-[11.5px] text-fg-subtle tabular-nums shrink-0">{fmtNumber(Math.round(doc.size / 1024))} {t("KB ·")} {fmtDateTime(doc.createdAt)}</span>
              {doc.sentAt && <span className="text-[11.5px] text-success shrink-0">· {t("beim Lager")}</span>}
              {d.editable && !d.approvedAt && (
                <button onClick={() => remove.mutate(doc.id)} className="ml-auto size-7 grid place-items-center rounded-lg text-fg-subtle hover:text-danger hover:bg-surface-2 shrink-0" aria-label={t("Dokument entfernen")}><Trash2 className="size-3.5" /></button>
              )}
            </li>
          ))}
        </ul>
      )}
      {d.approvedAt ? (
        <p className="mt-3 flex items-center gap-1.5 text-[12.5px] text-success"><CheckCircle2 className="size-4" /> {t("Freigegeben")} {fmtDateTime(d.approvedAt)}{d.approvedBy ? ` · ${d.approvedBy}` : ""}</p>
      ) : null}
      {d.editable && (
        <div className="mt-3 flex flex-wrap gap-2">
          <input ref={fileRef} type="file" accept="application/pdf" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) upload.mutate(f); e.target.value = ""; }} />
          <Button size="sm" icon={<Upload className="size-3.5" />} loading={upload.isPending} onClick={() => fileRef.current?.click()}>{d.documents.length ? t("Weiteres PDF hochladen") : t("PDF hochladen")}</Button>
          {d.documents.length > 0 && !d.approvedAt && <Button size="sm" variant="primary" icon={<FileCheck2 className="size-3.5" />} loading={approve.isPending} onClick={() => approve.mutate()}>{t("Geprüft – freigeben")}</Button>}
        </div>
      )}
    </section>
  );
}

/** Einstellung je Kunde: Prüfung an/aus, Grenzen für Warenwert und Gewicht */
export function ExportDocumentSettingsCard() {
  const { tenantId, role } = useTenant();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["export-doc-settings", tenantId], queryFn: () => api.orders.exportDocumentSettings(tenantId) });
  const [s, setS] = useState<ExportDocumentSettings | null>(null);
  useEffect(() => { if (q.data) setS(q.data); }, [q.data]);
  const save = useMutation({
    mutationFn: (v: ExportDocumentSettings) => api.orders.saveExportDocumentSettings(tenantId, v),
    onSuccess: () => { toast.success(t("Gespeichert")); void qc.invalidateQueries({ queryKey: ["export-doc-settings", tenantId] }); },
    onError: (e) => toast.error(t("Nicht gespeichert"), { description: errMsg(e) }),
  });
  const editable = canManage(role);
  if (!s) return null;
  const dirty = JSON.stringify(s) !== JSON.stringify(q.data);
  return (
    <Card className="p-5 mb-4">
      <h2 className="font-semibold text-[14px] flex items-center gap-2"><Globe2 className="size-4 text-fg-muted" /> {t("Ausfuhrbegleitdokument")}</h2>
      <p className="text-[12.5px] text-fg-muted mt-0.5">{t("Aufträge in Drittländer (außerhalb der EU) ab dieser Grenze werden zurückgehalten, bis das Ausfuhrbegleitdokument im Auftrag hochgeladen und freigegeben ist.")}</p>
      <label className="mt-3 flex items-center gap-2 text-[13px]">
        <input type="checkbox" checked={s.enabled} disabled={!editable} onChange={(e) => setS({ ...s, enabled: e.target.checked })} className="size-4 accent-[var(--color-ink)]" />
        {t("Prüfung aktiv")}
      </label>
      <div className="mt-3 grid gap-3 sm:grid-cols-2">
        <label className="text-[12.5px] text-fg-muted">{t("Warenwert ab (€)")}
          <Input className="mt-1" type="number" min={0} step={1} disabled={!editable || !s.enabled} value={s.valueThresholdEur} onChange={(e) => setS({ ...s, valueThresholdEur: Number(e.target.value) || 0 })} />
        </label>
        <label className="text-[12.5px] text-fg-muted">{t("Gewicht ab (kg)")}
          <Input className="mt-1" type="number" min={0} step={1} disabled={!editable || !s.enabled} value={s.weightThresholdKg} onChange={(e) => setS({ ...s, weightThresholdKg: Number(e.target.value) || 0 })} />
        </label>
      </div>
      {editable && (
        <div className="mt-4 flex justify-end gap-2 items-center">
          {dirty && <span className="text-[12px] text-fg-muted">{t("Ungespeicherte Änderungen")}</span>}
          <Button size="sm" variant="primary" disabled={!dirty} loading={save.isPending} onClick={() => save.mutate(s)}>{t("Speichern")}</Button>
        </div>
      )}
    </Card>
  );
}
